import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import axios, { type AxiosError } from "axios";

export const API_TRACE_HEADER = "X-AH-Trace-Id";
export const UNEXPECTED_BAD_REQUEST_CODE = "unexpected_bad_request";
export const VALIDATION_ERROR_CODE = "validation_failed";
export const CONNECTION_FAILURE_MESSAGE = "We could not connect. Check your connection and try again.";

export type ApiErrorCategory = "connection" | "validation" | "business" | "unexpected";
/**
 * TODO(#4907): Remove the legacy source, exact-text mapping types/options, and old-body decoding
 * below after #4907 and every linked API migration subtask emit only the v1 Problem Details profile.
 */
export type ApiErrorSource = "problem-details" | "legacy" | "unknown" | "network";
export type ApiFieldErrors = Readonly<Record<string, readonly string[]>>;

export type ApiErrorResult = {
  category: ApiErrorCategory;
  source: ApiErrorSource;
  status?: number;
  code?: string;
  title?: string;
  detail?: string;
  fieldErrors: ApiFieldErrors;
  traceId?: string;
  freshState?: unknown;
  /** False for every legacy body. Its text must not be rendered without an exact module mapping. */
  trusted: boolean;
};

export type KnownLegacyError = {
  code: string;
  title: string;
  detail: string;
};

export type ApiErrorReaderOptions = {
  headers?: unknown;
  knownLegacyText?: Readonly<Record<string, KnownLegacyError>>;
};

export type AttorneyHubFetchBaseQueryError = FetchBaseQueryError & {
  attorneyHubError: ApiErrorResult;
};

export function readRtkQueryError(
  error: FetchBaseQueryError | AttorneyHubFetchBaseQueryError,
  options: ApiErrorReaderOptions = {},
): ApiErrorResult {
  if ("attorneyHubError" in error) return error.attorneyHubError;
  return readApiError(error, options);
}

export function attachRtkQueryError(
  error: FetchBaseQueryError,
  result: ApiErrorResult,
): AttorneyHubFetchBaseQueryError {
  return Object.assign({}, error, { attorneyHubError: result }) as AttorneyHubFetchBaseQueryError;
}

export function adaptAxiosError(
  error: unknown,
  options: Omit<ApiErrorReaderOptions, "headers"> = {},
): ApiErrorResult | undefined {
  if (!axios.isAxiosError(error)) return undefined;

  const axiosError = error as AxiosError;
  if (!axiosError.response) return connectionFailure();

  return readApiError(
    { status: axiosError.response.status, data: axiosError.response.data },
    { ...options, headers: axiosError.response.headers },
  );
}

export function readApiError(error: unknown, options: ApiErrorReaderOptions = {}): ApiErrorResult {
  if (isRecord(error) && isApiErrorResult(error.attorneyHubError)) return error.attorneyHubError;
  if (isRecord(error) && isConnectionStatus(error.status)) return connectionFailure();

  const wrapper = isRecord(error) ? error : undefined;
  const body = wrapper && "data" in wrapper ? wrapper.data : error;
  const status = numericStatus(wrapper, body);
  const traceId = readHeader(options.headers, API_TRACE_HEADER)
    ?? getNonEmptyString(isRecord(body) ? body.traceId : undefined);
  const fieldErrors = readFieldErrors(body);

  if (isProblemDetails(body, status, traceId)) {
    return {
      category: body.code === UNEXPECTED_BAD_REQUEST_CODE
        ? "unexpected"
        : body.code === VALIDATION_ERROR_CODE || Object.keys(fieldErrors).length > 0
          ? "validation"
          : "business",
      source: "problem-details",
      status: body.status,
      code: body.code,
      title: body.title,
      detail: body.detail,
      fieldErrors,
      traceId: body.traceId,
      freshState: body.freshState,
      trusted: true,
    };
  }

  // TODO(#4907): Remove this exact-text migration adapter with the legacy response support above.
  const mappedLegacyText = legacyTextCandidates(body)
    .map((text) => ({ text, mapped: options.knownLegacyText?.[text] }))
    .find((candidate) => candidate.mapped);
  if (mappedLegacyText?.mapped) {
    return {
      category: "business",
      source: "legacy",
      status,
      code: mappedLegacyText.mapped.code,
      title: mappedLegacyText.mapped.title,
      detail: mappedLegacyText.mapped.detail,
      fieldErrors,
      traceId,
      trusted: true,
    };
  }

  if (Object.keys(fieldErrors).length > 0) {
    return {
      category: "validation",
      source: "legacy",
      status,
      code: VALIDATION_ERROR_CODE,
      fieldErrors,
      traceId,
      trusted: false,
    };
  }

  return {
    category: "unexpected",
    source: body === error ? "unknown" : "legacy",
    status,
    code: status === 400 ? UNEXPECTED_BAD_REQUEST_CODE : undefined,
    fieldErrors: {},
    traceId,
    trusted: false,
  };
}

export function getApiErrorUserMessage(error: ApiErrorResult): string | undefined {
  if (error.category === "connection") return CONNECTION_FAILURE_MESSAGE;
  if (error.category === "unexpected" && error.status !== undefined) {
    return error.traceId
      ? `We could not complete this action. Try again. If this happens again, contact support and give them reference ${error.traceId}.`
      : "We could not complete this action. Try again. If this happens again, contact support.";
  }
  if (error.trusted) return error.detail ?? error.title;

  return undefined;
}

function connectionFailure(): ApiErrorResult {
  return { category: "connection", source: "network", fieldErrors: {}, trusted: true };
}

function numericStatus(wrapper: Record<string, unknown> | undefined, body: unknown): number | undefined {
  if (typeof wrapper?.status === "number") return wrapper.status;
  if (wrapper?.status === "PARSING_ERROR" && typeof wrapper.originalStatus === "number") return wrapper.originalStatus;
  if (isRecord(body) && typeof body.status === "number") return body.status;
  if (isRecord(body) && typeof body.statusCode === "number") return body.statusCode;
  return undefined;
}

function isProblemDetails(
  value: unknown,
  wrapperStatus: number | undefined,
  traceId: string | undefined,
): value is Record<string, unknown> & {
  status: number;
  code: string;
  title: string;
  detail: string;
  traceId: string;
} {
  return isRecord(value)
    && typeof value.status === "number"
    && (wrapperStatus === undefined || value.status === wrapperStatus)
    && getNonEmptyString(value.code) !== undefined
    && getNonEmptyString(value.title) !== undefined
    && getNonEmptyString(value.detail) !== undefined
    && getNonEmptyString(value.traceId) !== undefined
    && value.traceId === traceId;
}

function readFieldErrors(body: unknown): ApiFieldErrors {
  if (!isRecord(body)) return {};
  const nested = normalizeFieldErrors(body.errors);
  if (Object.keys(nested).length > 0) return nested;

  const contractFields = new Set([
    "type", "status", "statusCode", "code", "title", "detail", "traceId", "freshState", "message", "error",
  ]);
  return normalizeFieldErrors(Object.fromEntries(
    Object.entries(body).filter(([key]) => !contractFields.has(key)),
  ));
}

function normalizeFieldErrors(value: unknown): ApiFieldErrors {
  if (!isRecord(value)) return {};

  const result: Record<string, readonly string[]> = {};
  for (const [field, messages] of Object.entries(value)) {
    if (typeof messages === "string" && messages.trim()) {
      result[field] = [messages];
      continue;
    }
    if (Array.isArray(messages)) {
      const strings = messages.filter((message): message is string => typeof message === "string");
      if (strings.length > 0) result[field] = strings;
    }
  }
  return result;
}

function legacyTextCandidates(body: unknown): string[] {
  if (typeof body === "string") return body.trim() ? [body] : [];
  if (!isRecord(body)) return [];

  const direct = [body.message, body.error]
    .map(getNonEmptyString)
    .filter((value): value is string => value !== undefined);
  const fieldMessages = Object.values(readFieldErrors(body)).flat();
  return [...direct, ...fieldMessages];
}

function readHeader(headers: unknown, name: string): string | undefined {
  if (!headers) return undefined;
  if (typeof Headers !== "undefined" && headers instanceof Headers) return getNonEmptyString(headers.get(name));
  if (isRecord(headers) && typeof headers.get === "function") {
    const get = headers.get as (this: Record<string, unknown>, header: string) => unknown;
    return getNonEmptyString(get.call(headers, name));
  }
  if (!isRecord(headers)) return undefined;

  const entry = Object.entries(headers).find(([key]) => key.toLowerCase() === name.toLowerCase());
  const value = entry?.[1];
  return Array.isArray(value) ? getNonEmptyString(value[0]) : getNonEmptyString(value);
}

function isConnectionStatus(value: unknown): boolean {
  return value === "FETCH_ERROR" || value === "TIMEOUT_ERROR";
}

function isApiErrorResult(value: unknown): value is ApiErrorResult {
  return isRecord(value)
    && typeof value.category === "string"
    && typeof value.source === "string"
    && typeof value.trusted === "boolean"
    && isRecord(value.fieldErrors);
}

function getNonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
